import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { getIcon, CURATED_ICONS, AVAILABLE_ICONS } from '../services/iconRegistry';
import { ChevronDown } from 'lucide-react';

interface IconPickerProps {
  value: string;
  onChange: (iconName: string) => void;
  placeholder?: string;
  className?: string;
  /** `dark` (default) for dark panels; `light` for the ruby-on-light pages. */
  variant?: 'light' | 'dark';
  /** Taller trigger (44px) for touch-sized rows. */
  size?: 'sm' | 'md';
}

export default function IconPicker({
  value,
  onChange,
  placeholder = 'Select icon',
  className = '',
  variant = 'dark',
  size = 'sm',
}: IconPickerProps) {
  const isLight = variant === 'light';
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left });
    }
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition]);

  // Keep focus on search input while dropdown is open
  useEffect(() => {
    if (open && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [open, search]);

  const SelectedIcon = value ? getIcon(value) : null;

  // When searching, show flat filtered results
  const isSearching = search.length > 0;
  const searchResults = isSearching
    ? AVAILABLE_ICONS.filter((name) =>
        name.toLowerCase().includes(search.toLowerCase())
      ).slice(0, 60)
    : [];

  const renderIconButton = (name: string, keyPrefix?: string) => {
    const Icon = getIcon(name);
    return (
      <button
        key={keyPrefix ? `${keyPrefix}-${name}` : name}
        type="button"
        onClick={() => {
          onChange(name);
          setOpen(false);
        }}
        title={name}
        className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${
          isLight ? 'hover:bg-rose-50 hover:text-[#DC143C]' : 'hover:bg-dark-700'
        } ${
          value === name
            ? isLight
              ? 'text-[#DC143C] bg-rose-50 ring-1 ring-[#DC143C]/40'
              : 'text-rose-400 bg-rose-500/10 ring-1 ring-rose-500/30'
            : isLight ? 'text-stone-600' : 'text-gray-300'
        }`}
      >
        <Icon className="w-4 h-4" />
      </button>
    );
  };

  return (
    <div className={`relative ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          setOpen(!open);
          setSearch('');
        }}
        className={`flex items-center gap-2 rounded-lg px-3 w-full transition-colors focus:outline-none ${
          size === 'md' ? 'min-h-[44px] text-sm' : 'py-1.5 text-xs'
        } ${
          isLight
            ? `bg-white border text-stone-900 hover:border-stone-400 focus:border-[#DC143C] focus:ring-2 focus:ring-[#DC143C]/15 ${open ? 'border-[#DC143C] ring-2 ring-[#DC143C]/15' : 'border-stone-300'}`
            : 'bg-dark-900 border border-white/10 text-white hover:border-rose-500 focus:border-rose-500'
        }`}
      >
        {SelectedIcon && <SelectedIcon className={`w-3.5 h-3.5 ${isLight ? 'text-[#DC143C]' : 'text-rose-400'}`} />}
        <span className={`truncate ${value ? (isLight ? 'text-stone-900' : 'text-white') : (isLight ? 'text-stone-400' : 'text-gray-500')}`}>
          {value || placeholder}
        </span>
        <ChevronDown className={`w-3 h-3 ml-auto shrink-0 transition-transform ${open ? 'rotate-180' : ''} ${isLight ? 'text-stone-400' : 'text-gray-500'}`} />
      </button>

      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            className={`fixed w-80 border overflow-hidden ${
              isLight ? 'bg-white border-stone-200 rounded-xl shadow-2xl' : 'bg-dark-800 border-white/10 rounded-lg shadow-xl'
            }`}
            style={{ top: pos.top, left: pos.left, zIndex: 9999 }}
          >
            <div className={`p-2 border-b ${isLight ? 'border-stone-100' : 'border-white/10'}`}>
              <input
                ref={searchInputRef}
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search icons..."
                className={`w-full rounded-lg px-2.5 text-xs focus:outline-none ${
                  isLight
                    ? 'bg-stone-50 border border-stone-200 py-2 text-stone-900 placeholder-stone-400 focus:border-[#DC143C] focus:bg-white'
                    : 'bg-dark-900 border border-white/10 py-1 text-white focus:border-rose-500'
                }`}
              />
            </div>
            <div className="max-h-64 overflow-y-auto p-2">
              {value && (
                <button
                  type="button"
                  onClick={() => {
                    onChange('');
                    setOpen(false);
                  }}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded mb-1 ${
                    isLight ? 'text-stone-500 hover:bg-rose-50 hover:text-[#DC143C]' : 'text-gray-400 hover:bg-dark-700'
                  }`}
                >
                  Clear selection
                </button>
              )}

              {isSearching ? (
                <>
                  <div className="flex flex-wrap gap-1">
                    {searchResults.map(name => renderIconButton(name))}
                  </div>
                  {searchResults.length === 0 && (
                    <p className={`px-2 py-3 text-xs text-center ${isLight ? 'text-stone-400' : 'text-gray-500'}`}>
                      No icons found
                    </p>
                  )}
                </>
              ) : (
                Object.entries(CURATED_ICONS).map(([category, icons]) => (
                  <div key={category} className="mb-3">
                    <p className={`text-[10px] uppercase tracking-wider font-bold mb-1 px-1 ${isLight ? 'text-stone-400' : 'text-gray-500'}`}>
                      {category}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {icons.map((name) => {
                        try {
                          return renderIconButton(name, category);
                        } catch {
                          return null;
                        }
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
